import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import Cookies from "js-cookie";
import jwtParse from "./helper/jwtParse";
import { getAccessToken, getTokenRefresh, signupGamer, socialSignupGamer, sendSignupVerificationCode, verifySignupCode } from "./gridAuthClient";
import PasswordPromptModal from "./helper/accountLinking";
import { AppContext } from "context/AppContext";
import { getUserSessionHistory } from "./session_history/chatSession";
import { getUserChatSubscription } from "./session_history/userChatSubscription";
import { getUserSubscription } from "./session_history/userSubscriptions"; // ✅ Add this import
import { getValidTimestamp } from "./helper/sessionUtils";

const AuthContext = createContext({
  user: null,
  loggedIn: false,
  loggedOut: false,
  chatSubscription: null,
  subscription: null, // ✅ Add Stripe subscription for UI display
  login: async () => {},
  logout: () => {},
  socialLogin: async () => {},
  refreshSubscription: async () => {},
});

const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds

export function AuthProvider({ children }) {
  const { setSideMessages, setMessages, setResponseMessages, setShowSuggestions } = useContext(AppContext);
  const [chatSubscription, setChatSubscription] = useState(null); // For chat API
  const [subscription, setSubscription] = useState(null); // ✅ For UI display (Stripe)
  const history = useHistory();
  
  const [user, setUser] = useState(() => {
    const token = Cookies.get("authToken");
    if (token) {
      const decoded = jwtParse(token);
      return {
        id: decoded.nameid,
        email: decoded.email,
        username: decoded.unique_name,
      };
    }
    return null;
  });
  
  const [loggedIn, setLoggedIn] = useState(!!user);
  const [loggedOut, setLoggedOut] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [socialContext, setSocialContext] = useState(null);
  const [pendingUserData, setPendingUserData] = useState(null);
  

  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // reset the inactivity timer
  const resetInactivityTimer = () => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    // Only set timer if user is logged in
    if (loggedIn && user) {
      inactivityTimerRef.current = setTimeout(() => {
        console.log("[AuthProvider] Inactivity timeout reached, logging out");
        handleSessionTimeout();
      }, INACTIVITY_TIMEOUT);
    }
  };

  //  handle session timeout
  const handleSessionTimeout = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    
    // perform logout
    Cookies.remove("authToken");
    Cookies.remove("refreshToken");
    setUser(null);
    setLoggedIn(false);
    setLoggedOut(true);
    setMessages([]);
    setResponseMessages([]);
    setSideMessages([]);
    setShowSuggestions(true);
    setPendingUserData(null);
    localStorage.removeItem('sessionId');
    
    toast({
      title: "Session timed out",
      status: "info",
      position: "top",
      duration: 5000,
      isClosable: true,
    });
    
    console.log("[AuthProvider] Session timeout - navigation to /");
    history.push("/");
  };

  useEffect(() => {
    if (!loggedIn || !user) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ];

    const handleActivity = () => {
      resetInactivityTimer();
    };

    // event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // start the timer
    resetInactivityTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [loggedIn, user]); 

  const refreshSubscription = async () => {
    if (!user) return;
    
    try {
      // ✅ Load chat subscription (for API limits)
      const chatSub = await getUserChatSubscription({ userId: String(user.id) });
      console.log("[AuthProvider] chat subscription:", chatSub);
      setChatSubscription(chatSub);
    } catch (err) {
      console.warn("Could not refresh chat subscription:", err);
      setChatSubscription({
        chat_subscription_id: 1,
        daily_limit: 100
      });
    }

    try {
      // ✅ Load Stripe subscription (for UI display)
      const stripeSub = await getUserSubscription({ userId: String(user.id) });
      console.log("[AuthProvider] stripe subscription:", stripeSub);
      setSubscription(stripeSub);
    } catch (err) {
      console.warn("Could not refresh stripe subscription:", err);
      setSubscription([]);
    }
  };
  
  const login = async (username, password) => {
    setMessages([]);
    setResponseMessages([]);
    setSideMessages([]);
    setShowSuggestions(true);
    
    try {
      const resp = await getAccessToken(username, password);
      if (resp.message === "Failed") throw new Error("Invalid Login");
      
      const { token, refreshToken, userId, email, userName } = resp.response;
      Cookies.set("authToken", token);
      Cookies.set("refreshToken", refreshToken);
      
      setUser({ id: String(userId), email, username: userName });
      setLoggedIn(true);

      // Load session history
      try {
        const sessionId = getValidTimestamp(); 
        localStorage.setItem('sessionId', sessionId);
        const history = await getUserSessionHistory({
          userId: String(userId),
          sessionId,
          page: 1,
          size: 100,
        });
        setSideMessages(history);
      } catch (err) {
        setSideMessages([]);
      }

      // ✅ Load chat subscription
      try {
        const chatSub = await getUserChatSubscription({ userId: String(userId) });
        console.log("[AuthProvider] chat subscription on login:", chatSub);
        setChatSubscription(chatSub);
      } catch (err) {
        console.warn("[AuthProvider] could not load chat subscription:", err);
        setChatSubscription({
          chat_subscription_id: 1,
          daily_limit: 100
        });
      }

      // ✅ Load Stripe subscription
      try {
        const stripeSub = await getUserSubscription({ userId: String(userId) });
        console.log("[AuthProvider] stripe subscription on login:", stripeSub);
        setSubscription(stripeSub);
      } catch (err) {
        console.warn("[AuthProvider] could not load stripe subscription:", err);
        setSubscription([]);
      }

      history.push("/admin/dashboard");
      return { success: true };
    } catch (err) {
      console.error("[AuthProvider] Login failed:", err);
      throw err;
    }
  };

  const socialLogin = async (email, uid, isGoogle) => {
    setMessages([]);
    setResponseMessages([]);
    setSideMessages([]);
    setShowSuggestions(true);
    
    try {
      const initial = await getAccessToken(email, uid, { 
        isSocialLogin: true, 
        isGoogle, 
        isAppleLogin: !isGoogle,
      });
      
      if (!initial.status && initial.response.returnValue === 9) {
        const password = await new Promise((res, rej) => {
          setSocialContext({ email, uid, isGoogle, res, rej, accountLabel });
          setModalOpened(true);
        });
        
        setModalOpened(false);
        setSocialContext(null);
        
        const second = await getAccessToken(email, password, {
          isSocialLogin: true,
          isGoogle,
          isAppleLogin: !isGoogle,
          socialToken: uid,
        });
        
        if (!second.status) throw new Error("Social link failed");
        
        const { token, refreshToken, userId, email: em, userName } = second.response;
        Cookies.set("authToken", token);
        Cookies.set("refreshToken", refreshToken);
        
        setUser({ id: String(userId), email: em, username: userName });
        setLoggedIn(true);

        // Load session history
        try {
          const sessionId = getValidTimestamp(); 
          localStorage.setItem('sessionId', sessionId);
          const history = await getUserSessionHistory({
            userId: String(userId),
            sessionId,
            page: 1,
            size: 100,
          });
          setSideMessages(history);
        } catch (err) {
          setSideMessages([]);
        }

        // ✅ Load both subscriptions
        try {
          const chatSub = await getUserChatSubscription({ userId: String(userId) });
          setChatSubscription(chatSub);
        } catch (err) {
          setChatSubscription({ chat_subscription_id: 1, daily_limit: 100 });
        }

        try {
          const stripeSub = await getUserSubscription({ userId: String(userId) });
          setSubscription(stripeSub);
        } catch (err) {
          setSubscription([]);
        }

        history.push("/admin/dashboard");
        return;
      }
      
      if (initial.message === "Failed") throw new Error("Social login failed");
      
      const { token, refreshToken, userId, email: em, userName } = initial.response;
      Cookies.set("authToken", token);
      Cookies.set("refreshToken", refreshToken);
      
      setUser({ id: String(userId), email: em, username: userName });
      setLoggedIn(true);
      
      history.push("/admin/dashboard");
    } catch (err) {
      console.error("[AuthProvider] Social login error:", err);
      throw err;
    }
  };

  const socialSignup = async (firebaseUser, isGoogle, additionalUsername = null) => {
    setMessages([]);
    setResponseMessages([]);
    setSideMessages([]);
    setShowSuggestions(true);
    console.log("[AuthProvider] socialSignup called for", firebaseUser.email, "isGoogle?", isGoogle);
    
    try {
      const email = firebaseUser.email;
      const uid = firebaseUser.uid;
      const phoneNumber = firebaseUser.phoneNumber;
      const displayName = firebaseUser.displayName;
      
      let firstName = "";
      let lastName = "";
      if (displayName) {
        const nameParts = displayName.split(' ');
        if (nameParts.length > 0) {
          firstName = nameParts[0];
          if (nameParts.length > 1) {
            lastName = nameParts[nameParts.length - 1];
          }
        }
      }

      let username = additionalUsername;
      if (!username) {
        if (displayName) {
          username = displayName.replace(/\s+/g, '').toLowerCase();
        } else {
          username = email.split('@')[0];
        }
      }

      const signupData = {
        username: username,
        email: email,
        password: uid, 
        phoneNumber: phoneNumber || null,
        dateOfBirth: null, 
        firstName: firstName,
        lastName: lastName,
        gender: "male", 
        latitude: 0,
        longitude: 0,
        countryCodeName: "US", 
        isSocialLogin: true,
        isGoogle: isGoogle,
        isAppleLogin: !isGoogle,
        token: uid,
        playerId: "",
      };

      const signupResp = await socialSignupGamer(signupData);
      console.log("[AuthProvider] social signup response:", signupResp);
      
      if (!signupResp.status) {
        throw new Error(signupResp.errorMessage || "Social signup failed");
      }

      console.log("[AuthProvider] Social signup successful, logging in user directly");
      
      const loginOptions = {
        isSocialLogin: true,
        isGoogle: isGoogle,
        isAppleLogin: !isGoogle,
        socialToken: uid
      };

      const loginResp = await getAccessToken(username, uid, loginOptions);
      console.log("[AuthProvider] auto-login after social signup response:", loginResp);
      
      if (loginResp.message === "Failed") {
        throw new Error("Social signup successful but auto-login failed");
      }

      const { token, refreshToken, userId, email: userEmail, userName } = loginResp.response;
      Cookies.set("authToken", token);
      Cookies.set("refreshToken", refreshToken);
      console.log("[AuthProvider] tokens stored after social signup");
      
      setUser({ id: String(userId), email: userEmail, username: userName });
      setLoggedIn(true);

      try {
        const sessionId = getValidTimestamp(); 
        localStorage.setItem('sessionId', sessionId);
        const sessionHistory = await getUserSessionHistory({
          userId: String(userId),
          sessionId,
          page: 1,
          size: 100,
        });
        setSideMessages(sessionHistory);
      } catch (err) {
        setSideMessages([]);
      }

      console.log("[AuthProvider] navigation to /admin/dashboard after social signup");
      history.push("/admin/dashboard");
      
      return { success: true };
      
    } catch (err) {
      console.error("[AuthProvider] Social signup failed:", err);
      throw err;
    }
  };

  const signup = async (username, email, password, phoneNumber, dateOfBirth, additionalData = {}) => {
    setMessages([]);
    setResponseMessages([]);
    setSideMessages([]);
    setShowSuggestions(true);
    console.log("[AuthProvider] signup called with", username, email);
    
    try {
      const signupData = {
        username,
        email,
        password,
        phoneNumber: phoneNumber || null,
        dateOfBirth,
        firstName: additionalData.firstName || "",
        lastName: additionalData.lastName || "",
        gender: additionalData.gender || "male",
        latitude: additionalData.latitude || 0,
        longitude: additionalData.longitude || 0,
        countryCodeName: additionalData.countryCodeName || "US",
        isSocialLogin: additionalData.isSocialLogin || false,
        isGoogle: additionalData.isGoogle || false,
        isAppleLogin: additionalData.isAppleLogin || false,
        token: additionalData.token || null,
        playerId: additionalData.playerId || "",
      };

      const signupResp = await signupGamer(signupData);
      console.log("[AuthProvider] signup response:", signupResp);
      
      if (!signupResp.status) {
        throw new Error(signupResp.errorMessage || "Signup failed");
      }

      setPendingUserData({
        username,
        password,
        email,
        token: signupResp.response?.token || "",
        needsToSendCode: false 
      });

      const verificationResp = await sendSignupVerificationCode(username, signupResp.response?.token || "");
      console.log("[AuthProvider] verification code sent:", verificationResp);
      
      if (!verificationResp.status) {
        throw new Error("Failed to send verification code");
      }

      return { needsVerification: true };
      
    } catch (err) {
      console.error("[AuthProvider] Signup failed:", err);
      throw err;
    }
  };

  const sendVerificationCode = async () => {
    console.log("[AuthProvider] sendVerificationCode called");
    
    if (!pendingUserData) {
      throw new Error("No pending user data found");
    }

    try {
      const token = pendingUserData.token || "";
      const sendResp = await sendSignupVerificationCode(pendingUserData.username, token);
      console.log("[AuthProvider] verification code sent:", sendResp);
      
      if (!sendResp.status) {
        throw new Error(sendResp.errorMessage || "Failed to send verification code");
      }
      
      setPendingUserData({
        ...pendingUserData,
        needsToSendCode: false
      });
      
      return sendResp;
    } catch (err) {
      console.error("[AuthProvider] Send verification code failed:", err);
      throw err;
    }
  };

  const verifyEmail = async (email, code) => {
    console.log("[AuthProvider] verifyEmail called with code:", code);
    
    if (!pendingUserData) {
      throw new Error("No pending user data found");
    }

    try {
      const token = pendingUserData.token || "";
      
      const verifyResp = await verifySignupCode(pendingUserData.username, code, token);
      console.log("[AuthProvider] verification response:", verifyResp);
      
      if (!verifyResp.status) {
        throw new Error(verifyResp.errorMessage || "Verification failed");
      }

      const loginOptions = {};
      if (pendingUserData.isSocialLogin) {
        loginOptions.isSocialLogin = true;
        loginOptions.isGoogle = pendingUserData.isGoogle;
        loginOptions.isAppleLogin = !pendingUserData.isGoogle;
        loginOptions.socialToken = pendingUserData.password; 
      }

      const loginResp = await getAccessToken(pendingUserData.username, pendingUserData.password, loginOptions);
      console.log("[AuthProvider] auto-login after verification response:", loginResp);
      
      if (loginResp.message === "Failed") {
        throw new Error("Verification successful but auto-login failed");
      }

      const { token: authToken, refreshToken, userId, email: userEmail, userName } = loginResp.response;
      Cookies.set("authToken", authToken);
      Cookies.set("refreshToken", refreshToken);
      console.log("[AuthProvider] tokens stored after verification");
      
      setUser({ id: String(userId), email: userEmail, username: userName });
      setLoggedIn(true);

      try {
        const sessionId = getValidTimestamp(); 
        localStorage.setItem('sessionId', sessionId);
        const sessionHistory = await getUserSessionHistory({
          userId: String(userId),
          sessionId,
          page: 1,
          size: 100,
        });
        setSideMessages(sessionHistory);
      } catch (err) {
        setSideMessages([]);
      }

      setPendingUserData(null);

      console.log("[AuthProvider] navigation to /admin/dashboard after verification");
      history.push("/admin/dashboard");
      
    } catch (err) {
      console.error("[AuthProvider] Email verification failed:", err);
      throw err;
    }
  };

  const resendVerificationCode = async (email) => {
    console.log("[AuthProvider] resendVerificationCode called for:", email);
    
    if (!pendingUserData) {
      throw new Error("No pending user data found");
    }

    try {
      const token = pendingUserData.token || "";
      const resendResp = await sendSignupVerificationCode(pendingUserData.username, token);
      console.log("[AuthProvider] resend verification code response:", resendResp);
      
      if (!resendResp.status) {
        throw new Error(resendResp.errorMessage || "Failed to resend verification code");
      }
      
      return resendResp;
    } catch (err) {
      console.error("[AuthProvider] Resend verification code failed:", err);
      throw err;
    }
  };

  const logout = () => {
    Cookies.remove("authToken");
    Cookies.remove("refreshToken");
    setUser(null);
    setLoggedIn(false);
    setLoggedOut(true);
    setMessages([]); 
    setResponseMessages([]);
    setSideMessages([]);
    setShowSuggestions(true);
    setChatSubscription(null);
    setSubscription(null); // ✅ Clear Stripe subscription too
    localStorage.removeItem('sessionId'); 
    history.push("/");
  };

  const needsManualCodeSend = () => {
    return pendingUserData?.needsToSendCode || false;
  };

  const handleModalSubmit = (password) => {
    if (socialContext) socialContext.res(password);
    setModalOpened(false);
    setSocialContext(null);
  };
  
  const handleModalCancel = () => {
    if (socialContext) socialContext.rej(new Error("Canceled"));
    setModalOpened(false);
    setSocialContext(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loggedIn, 
      loggedOut, 
      chatSubscription, // For chat API (daily_limit, chat_subscription_id)
      subscription,     // ✅ For UI display (Stripe plan)
      login, 
      logout, 
      socialLogin, 
      refreshSubscription 
    }}>
      {children}
      <PasswordPromptModal
        opened={modalOpened}
        onSubmit={handleModalSubmit}
        onCancel={handleModalCancel}
        accountLabel={socialContext?.accountLabel}
      /> 
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);